import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-attempt";

/**
 * Reload the page bypassing any cached HTML. A plain location.reload() in
 * Safari can reuse the stale document that points at deleted chunks, so we
 * navigate to a unique URL that forces a fresh copy of index.html.
 */
export async function hardReload() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // ignore cleanup failures
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_v", String(Date.now()));
  window.location.replace(url.toString());
}

/**
 * Lazy import that recovers from stale deploy caches.
 * If a chunk is missing, force-reload a fresh copy of the site (at most once
 * per 30s). If it still fails, the error reaches ChunkErrorBoundary, which
 * shows a reload button instead of a blank screen.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last > 30000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        await hardReload();
        return new Promise<never>(() => {});
      }
      throw err;
    }
  });
}
